import {
  Avatar,
  Col,
  Edit,
  Form,
  Input,
  ListButton,
  Row,
  Select,
  Space,
  Upload,
  getValueFromEvent,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { HttpError, IResourceComponentsProps, file2Base64 } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { AddressFormItem } from 'components';
import { IClient, ITag } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAllTag } from 'services/tag';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';

const { Option } = Select;

export const ClientEdit: React.FC<IResourceComponentsProps> = () => {
  const [tags, setTags] = useState<ITag[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllTag();
        const tagsData = data.data;
        setTags(tagsData);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
      }
    })();
  }, []);

  const { formProps, saveButtonProps, queryResult } = useForm<IClient, HttpError>({
    metaData: {
      fields: [
        'id',
        'name',
        'email',
        'clientCode',
        'mobileNumber',
        {
          address: ['street', 'area', 'city', 'country', 'zip'],
        },
        {
          clientPhoto: [
            {
              avatar: ['url', 'thumbUrl'],
            },
          ],
        },
        {
          clientTags: [
            {
              tag: ['id'],
            },
          ],
        },
      ],
    },
  });

  const clientPhoto = queryResult?.data?.data.clientPhoto;
  let avatarURL;
  if (clientPhoto instanceof Array) {
    avatarURL = clientPhoto[clientPhoto?.length - 1]?.avatar?.url;
  }
  const selectedTags = queryResult?.data?.data?.clientTags?.map((tagObj) => tagObj.tag.id) || [];

  const getTagOptions = () => {
    return tags.map((tag) => (
      <Option key={tag.id} value={tag.id}>
        {tag.name}
      </Option>
    ));
  };

  const onFinish = async (values: any) => {
    let { avatar, tags } = values;
    const base64Files = [];

    if (avatar && !Array.isArray(avatar)) {
      base64Files.push(avatar);
    }

    if (avatar && avatar.length) {
      const file = avatar[0];
      const format = file.type || '';
      const size = file.size || 0;

      if (
        isJpgOrPngFile(format, file.name || '') &&
        isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)
      ) {
        if (file.originFileObj) {
          const base64String = await file2Base64(file);
          const { originFileObj, ...rest } = file;
          base64Files.push({ ...rest, base64String });
        } else {
          base64Files.push(file);
        }
      }
    }

    const tagsPayload = !!tags ? tags.map((tag: string) => ({ id: tag })) : [];

    return (
      formProps.onFinish &&
      formProps.onFinish({
        ...values,
        tags: tagsPayload,
        avatar: base64Files,
      })
    );
  };

  return (
    <Edit
      isLoading={queryResult?.isFetching}
      saveButtonProps={saveButtonProps}
      headerProps={{ extra: <ListButton /> }}
    >
      <Form
        {...formProps}
        layout="vertical"
        scrollToFirstError
        initialValues={{
          isActive: true,
          tags: selectedTags,
          ...formProps.initialValues,
        }}
        onFinish={onFinish}
      >
        <Row gutter={[64, 0]} wrap>
          <Col xs={24} lg={8}>
            <Space
              direction="vertical"
              style={{
                width: '100%',
                textAlign: 'center',
                marginBottom: '16px',
                marginTop: '6px',
              }}
            >
              <Form.Item
                name="avatar"
                valuePropName="file"
                getValueFromEvent={getValueFromEvent}
                noStyle
              >
                <Upload.Dragger
                  listType="picture"
                  accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                  maxCount={1}
                  beforeUpload={beforeImageUploadValidation}
                >
                  <Avatar
                    size={200}
                    style={{ objectFit: 'cover' }}
                    src={avatarURL || defaultAvatar}
                    alt="Store Location"
                  />
                  <p className="ant-upload-text">Drag & drop an image file in this area</p>
                </Upload.Dragger>
              </Form.Item>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label="Client Name"
              name="name"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  type: 'email',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Mobile Number"
              name="mobileNumber"
              rules={[
                {
                  pattern: /^(\+\d{1,3}[- ]?)?\d{8,12}$/,
                  message: 'Provide valid number',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Unique ID" name="clientCode">
              <Input />
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <Select
                showSearch
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select tags"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {getTagOptions()}
              </Select>
            </Form.Item>
            <AddressFormItem />
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
